import React from 'react';
import styled from '@emotion/styled';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Styled components for ranking question
const RankingContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
`;

const SortableRankingItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  margin: 0.5rem 0;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: white;
  cursor: grab;
  transition: all 0.2s ease;
  user-select: none;
  touch-action: none;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &.dragging {
    opacity: 0.8;
    background-color: #f5f5f5;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    cursor: grabbing;
  }

  &:active {
    cursor: grabbing;
  }
`;

const RankBox = styled.div`
  width: 30px;
  height: 30px;
  border: 1px solid #9ebe21;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 1rem;
  color: #9ebe21;
  font-weight: bold;
`;

const DragHandle = styled.div`
  margin-left: auto;
  color: #666;
  font-size: 1.2rem;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

// SortableItem component
const SortableItem = ({ id, choice, index, rank, disabled = false }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    data: {
      type: 'ranking-item',
      choice,
      index
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
    position: 'relative',
    backgroundColor: isDragging ? '#f8f8f8' : 'white',
    boxShadow: isDragging ? '0 5px 10px rgba(0,0,0,0.1)' : 'none',
    touchAction: 'none',
    cursor: disabled ? 'not-allowed' : isDragging ? 'grabbing' : 'grab'
  };

  return (
    <SortableRankingItem
      ref={setNodeRef}
      style={style}
      className={isDragging ? 'dragging' : ''}
      {...(!disabled ? attributes : {})}
      {...(!disabled ? listeners : {})}
    >
      <RankBox
        style={{
          backgroundColor: isDragging ? '#9ebe21' : 'white',
          color: isDragging ? 'white' : '#9ebe21',
          transition: 'all 0.2s ease'
        }}
      >
        {rank}
      </RankBox>
      <div
        style={{
          flex: 1,
          fontWeight: isDragging ? 'bold' : 'normal',
          transition: 'all 0.2s ease',
          userSelect: 'none'
        }}
      >
        {choice.choiceName}
      </div>
      <DragHandle
        style={{
          opacity: disabled ? 0.5 : 1,
          transition: 'all 0.2s ease',
          cursor: disabled ? 'not-allowed' : 'grab'
        }}
      >
        ☰
      </DragHandle>
    </SortableRankingItem>
  );
};

// Main RankingQuestion component
const RankingQuestion = ({ question, answers, handleRankingChange, disabled = false }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 2
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  // Get the current order of items (for display)
  const getCurrentOrder = () => {
    // If we have an answer, use the rank scale order (new order)
    if (answers[question.question_ID]?.questionRankScale_ID) {
      // Map the rank scale IDs to their corresponding choice IDs
      const rankScaleOrder = answers[question.question_ID].questionRankScale_ID;
      const choiceOrder = answers[question.question_ID].questionChoice_ID;

      // Create a mapping of rank scale to choice IDs
      const rankToChoiceMap = {};
      rankScaleOrder.forEach((rankId, index) => {
        rankToChoiceMap[rankId] = choiceOrder[index];
      });

      // Return the choice IDs in the order of the rank scale
      return question.RankScaleName_array.map(rank => rankToChoiceMap[rank.questionRankScale_ID]);
    }
    // Otherwise use the original order
    return question.choiceName_array.map(c => c.choiceName_ID);
  };

  const handleDragEnd = event => {
    // Remove focus from any focused input to trigger blur/save
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    try {
      const currentOrder = getCurrentOrder();
      const oldIndex = currentOrder.indexOf(active.id);
      const newIndex = currentOrder.indexOf(over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const newOrder = [...currentOrder];
      const temp = newOrder[oldIndex];
      newOrder[oldIndex] = newOrder[newIndex];
      newOrder[newIndex] = temp;

      const originalRankScaleIds = question.RankScaleName_array.map(r => r.questionRankScale_ID);

      const payload = {
        question_ID: question.question_ID,
        questionType_ID: question.questionType_ID,
        questionChoice_ID: newOrder,
        questionRankScale_ID: originalRankScaleIds
      };
      handleRankingChange(question.question_ID, payload);
    } catch (error) {
      console.error('Error in handleDragEnd:', error);
    }
  };

  // Get the current ranks (for display)
  const getCurrentRanks = () => {
    const currentOrder = getCurrentOrder();
    return currentOrder.map((_, idx) => idx + 1);
  };

  return (
    <RankingContainer style={{ opacity: disabled ? 0.6 : 1 }}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} autoScroll={false}>
        <SortableContext items={getCurrentOrder()} strategy={verticalListSortingStrategy}>
          {getCurrentOrder().map((choiceId, index) => {
            // Find the choice object for this ID
            const choice = question.choiceName_array.find(c => c.choiceName_ID === choiceId);
            if (!choice) return null;

            // Get the rank for this item
            const ranks = getCurrentRanks();
            const rank = ranks[index];

            return <SortableItem key={choiceId} id={choiceId} choice={choice} index={index} rank={rank} disabled={disabled}/>;
          })}
        </SortableContext>
      </DndContext>
    </RankingContainer>
  );
};

export default RankingQuestion;
